import { computed } from '@preact/signals';

import type { MemberView } from '@amumin/api-client';
import { weekdayIndex, weekdayOf } from '@amumin/domain';
import { formatEuro } from '@amumin/domain/samen';

import { HOUSEHOLD_TZ } from '../lib/dates.js';
import { householdType } from '../state/householdType.js';
import { appText, context, household, locale, messages, ownMemberId } from '../state/session.js';

// Samen wonen in de webapp (golf 8, 0049; docs/onderzoek/golf8-samenwonen-en-ouderen.md §2.2):
// gedeelde kleine hulpen voor Wie eet mee, Kosten delen, Reserveren en de widgets. De server is de
// bron van rechten en getallen; de webapp rekent alleen weergave (bedragen, namen, woorden).

/** Rol van deze login in het actieve huishouden. */
export const samenRole = computed<'owner' | 'parent' | 'child'>(() => {
  const ctx = context.value;
  const role = ctx?.households.find((h) => h.id === ctx.active_household_id)?.role;
  return role === 'owner' || role === 'parent' ? role : 'child';
});

export const isAdult = computed(() => samenRole.value !== 'child');

export const samenTz = computed(() => household.value?.timezone ?? HOUSEHOLD_TZ);

/** De woorden per soort (§2.9). */
export const samenWords = computed(() => {
  const w = appText.value.samen.words;
  const type = householdType.value;
  return {
    dinnerTitle: type === 'cohousing' ? w.dinnerTitleCohousing : w.dinnerTitle,
    costsTitle:
      type === 'cohousing'
        ? w.costsTitleCohousing
        : type === 'couple'
          ? w.costsTitleCouple
          : type === 'two_home'
            ? w.costsTitleTwoHome
            : w.costsTitle,
    emptyDinner:
      type === 'student' ? w.emptyStudent : type === 'cohousing' ? w.emptyCohousing : w.emptyShared,
    // Zelfde indeling als formerMemberSet in @amumin/db (de naam die het wissen echt schrijft).
    formerMember:
      type === 'student' || type === 'shared'
        ? w.formerShared
        : type === 'couple' || type === 'solo' || type === 'senior' || type === 'cohousing'
          ? w.formerCohousing
          : w.formerFamily,
    /** Weghalen zegt "uit huis halen" in plaats van "uit het gezin halen" (review golf 8 r2). */
    houseRemoval:
      type === 'student' ||
      type === 'shared' ||
      type === 'couple' ||
      type === 'solo' ||
      type === 'senior' ||
      type === 'cohousing',
  };
});

export function members(): MemberView[] {
  return household.value?.members ?? [];
}

export function memberOf(id: string | null | undefined): MemberView | undefined {
  return id ? members().find((m) => m.id === id) : undefined;
}

/** Naam van een lid; een vertrokken of gewist lid krijgt het woord uit de woordenset. */
export function nameOf(id: string | null | undefined): string {
  if (!id) return '';
  return memberOf(id)?.display_name ?? samenWords.value.formerMember;
}

export function isMe(id: string | null | undefined): boolean {
  return !!id && id === ownMemberId.value;
}

/** "ma" voor een datum (MO = 0). */
export function weekdayShort(date: string): string {
  return messages.value.weekdaysShort[weekdayIndex(weekdayOf(date))] ?? '';
}

/** "ma 28" voor de weekstrook. */
export function dayChip(date: string): string {
  return `${weekdayShort(date)} ${Number(date.slice(8, 10))}`;
}

export function euro(cents: number): string {
  return formatEuro(cents, locale.value);
}

/** "2 u 10 min" of "35 min" tot een tijdstip. */
export function leftUntil(ms: number, nowMs: number): string {
  const min = Math.max(0, Math.round((ms - nowMs) / 60_000));
  const h = Math.floor(min / 60);
  const m = min % 60;
  const c = appText.value.samen.bookings;
  if (h === 0) return c.minutes.replace('{n}', String(m));
  return m === 0
    ? c.hours.replace('{n}', String(h))
    : `${c.hours.replace('{n}', String(h))} ${c.minutes.replace('{n}', String(m))}`;
}

/** Duur in minuten als "90 min" of "1½ uur" (zonder breuken: "1 uur 30 min"). */
export function duration(minutes: number): string {
  const c = appText.value.samen.bookings;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return c.minutes.replace('{n}', String(m));
  if (m === 0) return c.hours.replace('{n}', String(h));
  return `${c.hours.replace('{n}', String(h))} ${c.minutes.replace('{n}', String(m))}`;
}
